import React, { useMemo, useState } from 'react';
import { useBlockLibrary } from '../../forms/formBuilder/blockLibraryContext';
import { FM_SMART_BLOCK_CATEGORIES } from '../../forms/formBuilder/constants';
import type { BuilderLibraryTab, BuilderBlockSource, LibraryBlockDef } from '../../forms/types';
import { resolveFormBuilderBlockIcon } from '../../forms/formBuilder/blockIcons';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { FormBuilderBlockIcon } from './FormBuilderBlockIcons';

const TABS: { key: BuilderLibraryTab; label: string }[] = [
  { key: 'patient', label: 'Patient Info' },
  { key: 'smart', label: 'Smart Blocks' },
  { key: 'basic', label: 'Basics' },
];

function accentFor(block: LibraryBlockDef): string | undefined {
  if (block.accentColor) return block.accentColor;
  if (block.source === 'patient') return 'var(--info)';
  if (block.source === 'basic') return 'var(--ink-3)';
  if (block.compliance) return 'var(--gold)';
  if (block.source === 'smart') return 'var(--ai-3)';
  return undefined;
}

export interface FormBuilderLibraryPanelProps {
  activeTab: BuilderLibraryTab;
  onTabChange: (tab: BuilderLibraryTab) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onClickAdd: (source: BuilderBlockSource, typeId: string) => void;
  onDragStart: (e: React.DragEvent, source: BuilderBlockSource, typeId: string) => void;
  onDragEnd: () => void;
}

export function FormBuilderLibraryPanel({
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  onClickAdd,
  onDragStart,
  onDragEnd,
}: FormBuilderLibraryPanelProps) {
  const q = search.toLowerCase();
  const library = useBlockLibrary();
  const counts = useMemo(
    () => ({
      patient: library.filter((b) => b.source === 'patient').length,
      smart: library.filter((b) => b.source === 'smart').length,
      basic: library.filter((b) => b.source === 'basic').length,
    }),
    [library],
  );

  const filtered = library
    .filter((item) => item.source === activeTab)
    .filter((item) => !q || item.label.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q));

  const smartByCategory = useMemo(() => {
    if (activeTab !== 'smart') return [];
    return FM_SMART_BLOCK_CATEGORIES.map((category) => ({
      category,
      items: filtered.filter((b) => (b.category ?? 'Other') === category),
    })).filter((g) => g.items.length > 0);
  }, [activeTab, filtered]);

  return (
    <aside className="fm-builder-library flex shrink-0 flex-col border-r border-[var(--cream-2)]">
      <div className="px-5 pb-3 pt-[18px]">
        <p className="mb-3 font-[var(--font-mono)] text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Block library
        </p>
        <div className="mb-3 flex gap-0.5 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] p-0.5">
          {TABS.map((tab) => {
            const on = activeTab === tab.key;
            if (tab.key === 'smart') {
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    onTabChange(tab.key);
                  }}
                  className={cn(
                    'fm-library-tab-smart flex flex-1 items-center justify-center gap-1 rounded-[6px] border-0 px-1.5 py-[7px] text-[11.5px] font-semibold transition-all',
                    on && 'is-active',
                  )}
                >
                  <Icon
                    kind="sparkle"
                    size={11}
                    className={cn('shrink-0', on ? 'text-white' : 'text-[var(--ai-3)]')}
                  />
                  {on ? (
                    <span>Smart Blocks</span>
                  ) : (
                    <span className="fm-library-tab-smart-label">Smart Blocks</span>
                  )}
                  <span
                    className={cn(
                      'font-[var(--font-mono)] text-[10px]',
                      on ? 'text-white/80' : 'text-[var(--ink-4)]',
                    )}
                  >
                    {counts.smart}
                  </span>
                </button>
              );
            }
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  onTabChange(tab.key);
                }}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-[6px] border-0 px-2 py-[7px] text-[11.5px] transition-all',
                  on
                    ? 'bg-[var(--cream-1)] font-semibold text-[var(--ink-0)] shadow-[var(--sh-1)]'
                    : 'bg-transparent font-medium text-[var(--ink-3)]',
                )}
              >
                {tab.label}
                <span className="font-[var(--font-mono)] text-[10px] text-[var(--ink-4)]">{counts[tab.key]}</span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Icon
            kind="search"
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]"
          />
          <input
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            placeholder="Search blocks…"
            className="h-9 w-full rounded-lg border border-[var(--cream-2)] bg-[var(--cream-0)] py-0 pl-9 pr-3 text-[12.5px] text-[var(--ink-0)] outline-none focus:border-[var(--ai-3)] focus:bg-[var(--cream-1)] focus:shadow-[0_0_0_3px_var(--ai-tint)]"
          />
        </div>
      </div>

      <div className="cal-scroll flex-1 overflow-y-auto overscroll-contain px-3 pb-6">
        {activeTab === 'smart'
          ? smartByCategory.map(({ category, items }) => (
              <div key={category} className="mb-3.5">
                <div className="flex items-center justify-between px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
                  <span>{category}</span>
                  <span className="font-[var(--font-mono)] text-[var(--ink-4)]">{items.length}</span>
                </div>
                {items.map((block) => (
                  <LibraryBlockCard
                    key={block.id}
                    block={block}
                    onClick={() => {
                      onClickAdd(block.source, block.id);
                    }}
                    onDragStart={(e) => {
                      onDragStart(e, block.source, block.id);
                    }}
                    onDragEnd={onDragEnd}
                  />
                ))}
              </div>
            ))
          : filtered.map((block) => (
              <LibraryBlockCard
                key={block.id}
                block={block}
                onClick={() => {
                  onClickAdd(block.source, block.id);
                }}
                onDragStart={(e) => {
                  onDragStart(e, block.source, block.id);
                }}
                onDragEnd={onDragEnd}
              />
            ))}
      </div>

      <p className="flex items-center gap-1.5 border-t border-[var(--cream-2)] bg-[var(--cream-0)] px-5 py-2.5 text-[11px] leading-snug text-[var(--ink-4)]">
        <Icon kind="grip" size={12} className="shrink-0 text-[var(--ink-3)]" />
        Drag onto canvas, or click to append.
      </p>
    </aside>
  );
}

function LibraryBlockCard({
  block,
  onClick,
  onDragStart,
  onDragEnd,
}: {
  block: LibraryBlockDef;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  const [hover, setHover] = useState(false);
  const accent = accentFor(block);

  return (
    <button
      type="button"
      draggable
      onClick={onClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      className={cn(
        'mb-0.5 flex w-full items-start gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all',
        hover ? 'border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]' : 'border-transparent',
      )}
    >
      <div
        className={cn(
          'flex size-[30px] shrink-0 items-center justify-center rounded-[7px]',
          !hover && 'bg-[var(--cream-0)]',
        )}
        style={
          hover && accent
            ? { backgroundColor: `color-mix(in srgb, ${accent} 12%, var(--cream-1))`, color: accent }
            : accent
              ? { color: accent }
              : undefined
        }
      >
        <FormBuilderBlockIcon icon={resolveFormBuilderBlockIcon(block)} size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[12.5px] font-medium text-[var(--ink-0)]">{block.label}</span>
          {block.compliance ? <Icon kind="shield" size={10} className="shrink-0 text-[var(--gold)]" /> : null}
        </div>
        {block.desc ? (
          <p
            className={cn(
              'truncate text-[11px] text-[var(--ink-3)]',
              block.source === 'patient' && 'font-[var(--font-mono)]',
            )}
          >
            {block.desc}
          </p>
        ) : null}
      </div>
      {hover ? <Icon kind="plus" size={12} className="mt-1 shrink-0 text-[var(--ai-3)]" /> : null}
    </button>
  );
}
